import {
  AppAlert,
  AppBadge,
  AppBadgeForAno,
  AppLoadingSpinner,
  AppSectionTitle,
  AppTab,
  AppTabContent,
  AppTabList,
  AppTabTrigger,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useCommentMarkers } from '@opportunities/-hooks/useCommentMarkers'
import { useOpportunity } from '@opportunities/$opportunityId/-hooks/useOpportunity'
import { useOpportunityTasks } from '@opportunities/$opportunityId/-hooks/useOpportunityTasks'
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { OPPORTUNITY_TASK_STATUS_IDS } from 'shared'
import { useAnoMenuItems } from '@/hooks/useAnoMenuItems'
import { useAuthStore } from '@/stores/auth'
import { CollapsibleTabTrigger } from './-components/CollapsibleTabTrigger'
import { CommentsTab } from './-components/CommentsTab'
import { LogsTab } from './-components/LogsTab'
import { MembersTab } from './-components/MembersTab'
import { PropertiesTab } from './-components/PropertiesTab'
import { SalesTab } from './-components/SalesTab'
import { TasksTab } from './-components/TasksTab'

// パンくずリスト用のURLクエリパラメータ型定義
type OpportunitySearchParams = {
  tab?: string
  fromTab?: string
  fromPageLabel?: string
  fromPageLink?: string
}

export const Route = createFileRoute('/_authenticated/opportunities/$opportunityId/')({
  component: OpportunityDetailPage,
  validateSearch: (search: Record<string, unknown>): OpportunitySearchParams => {
    return {
      tab: typeof search.tab === 'string' ? search.tab : undefined,
      fromTab: typeof search.fromTab === 'string' ? search.fromTab : undefined,
      fromPageLabel: typeof search.fromPageLabel === 'string' ? search.fromPageLabel : undefined,
      fromPageLink: typeof search.fromPageLink === 'string' ? search.fromPageLink : undefined,
    }
  },
  staticData: {
    breadcrumbLabel: 'オポチュニティ詳細',
  },
})

function OpportunityDetailPage() {
  const { opportunityId } = useParams({ from: '/_authenticated/opportunities/$opportunityId/' })
  const { tab, fromTab, fromPageLabel, fromPageLink } = useSearch({
    from: '/_authenticated/opportunities/$opportunityId/',
  })
  const navigate = useNavigate()

  // ACLからeditable権限を取得（フックは条件分岐の前に呼び出す）
  const user = useAuthStore((state) => state.user)
  const editable = user?.acl?.opportunity?.update ?? false

  const parsedOpportunityId = Number.parseInt(opportunityId, 10)
  const { data: opportunity, isLoading, error } = useOpportunity(parsedOpportunityId)

  // タブバッジ用のデータ取得
  const { data: commentMarkers } = useCommentMarkers(parsedOpportunityId)
  const { data: tasksData } = useOpportunityTasks(parsedOpportunityId, {
    taskStatusId: OPPORTUNITY_TASK_STATUS_IDS.OPEN,
    memberId: user?.memberId,
  })

  const anoMenuUrls = useAnoMenuItems({
    ano: opportunity?.ano ?? null,
    salesforceProjectId: opportunity?.salesforce_project_id,
    pksId: opportunity?.pks_id,
  })

  const unreadCommentCount = commentMarkers?.count ?? 0
  const openTaskCount = tasksData?.total ?? 0

  // URLパラメータ未指定時はプロパティタブを表示
  const currentTab = tab || 'properties'

  // SQL売上一覧タブのヘッダー開閉状態
  const [isSalesHeaderOpen, setIsSalesHeaderOpen] = useState(true)

  // URLパラメータ連動によりタブ状態の共有・ブラウザバック時の復元を実現
  const handleTabChange = (value: string) => {
    navigate({
      to: '/opportunities/$opportunityId',
      params: { opportunityId },
      search: { tab: value, fromTab, fromPageLabel, fromPageLink },
      replace: true,
    })
  }

  if (isLoading) {
    return (
      <div className='flex h-full items-center justify-center'>
        <AppLoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className='p-8'>
        <AppAlert type='error'>オポチュニティの取得に失敗しました: {error.message}</AppAlert>
      </div>
    )
  }

  if (!opportunity) {
    return (
      <div className='p-8'>
        <AppAlert type='error'>オポチュニティが見つかりません</AppAlert>
      </div>
    )
  }

  return (
    <div className='grid h-full grid-rows-[auto_minmax(0,1fr)]'>
      <AppSectionTitle>
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center justify-between text-sm text-muted-foreground'>
            <div className='flex items-center gap-4'>
              <span>
                管理ID: <span className='font-normal'>{opportunity.id}</span>
              </span>
              {opportunity.disabled_at && <AppBadge color='gray'>インセンティブ対象外</AppBadge>}
            </div>
            <div className='flex items-center gap-3 pr-6'>
              <span>
                起票日:
                <span className='ml-1 font-normal'>
                  {opportunity.created_at
                    ? DateTime.fromSeconds(Number(opportunity.created_at)).toFormat('yyyy/MM/dd')
                    : '-'}
                </span>
              </span>
              <span>
                起票者: <span className='font-normal'>{opportunity.created_by_name || '-'}</span>
              </span>
            </div>
          </div>
          <div className='flex items-center gap-2 text-sm'>
            <span>{opportunity.customer_name || '(取引先未設定)'}</span>
            <span className='flex items-center gap-2'>
              {opportunity.salesforce_project_name || '(案件未設定)'}
              <AppBadgeForAno ano={opportunity.ano} menuUrls={anoMenuUrls} />
            </span>
          </div>
          <div className='text-xl font-medium leading-tight'>{opportunity.title}</div>
        </div>
      </AppSectionTitle>
      <AppTab value={currentTab} onValueChange={handleTabChange} className='grid h-full grid-rows-[auto_minmax(0,1fr)]'>
        <AppTabList>
          <AppTabTrigger value='properties'>プロパティ</AppTabTrigger>
          <AppTabTrigger value='comments'>
            <span className='flex items-center gap-1.5'>
              コメント一覧
              {unreadCommentCount > 0 && (
                <AppBadge color='destructive' className='h-5 min-w-5 px-1.5'>
                  {unreadCommentCount}
                </AppBadge>
              )}
            </span>
          </AppTabTrigger>
          <AppTabTrigger value='tasks'>
            <span className='flex items-center gap-1.5'>
              タスク一覧
              {openTaskCount > 0 && (
                <AppBadge color='blue' outline className='h-5 min-w-5 px-1.5'>
                  {openTaskCount}
                </AppBadge>
              )}
            </span>
          </AppTabTrigger>
          <AppTabTrigger value='members'>関係者一覧</AppTabTrigger>
          <CollapsibleTabTrigger
            value='sales'
            currentTab={currentTab}
            isHeaderOpen={isSalesHeaderOpen}
            onToggleHeader={() => setIsSalesHeaderOpen(!isSalesHeaderOpen)}
          >
            SQL化売上一覧
          </CollapsibleTabTrigger>
          <AppTabTrigger value='logs'>操作ログ</AppTabTrigger>
        </AppTabList>

        <AppTabContent value='properties' className='h-full'>
          <PropertiesTab opportunity={opportunity} editable={editable} />
        </AppTabContent>

        <AppTabContent value='comments' className='h-full overflow-hidden'>
          <CommentsTab opportunityId={parsedOpportunityId} editable={editable} />
        </AppTabContent>

        <AppTabContent value='tasks' className='h-full'>
          <TasksTab
            opportunityId={parsedOpportunityId}
            salesforceProjectId={opportunity.salesforce_project_id}
            editable={editable}
          />
        </AppTabContent>

        <AppTabContent value='members' className='h-full'>
          <MembersTab opportunityId={parsedOpportunityId} salesforceProjectId={opportunity.salesforce_project_id} />
        </AppTabContent>

        <AppTabContent value='sales' className='h-full'>
          <SalesTab opportunityId={parsedOpportunityId} isHeaderOpen={isSalesHeaderOpen} />
        </AppTabContent>

        <AppTabContent value='logs' className='h-full'>
          <LogsTab opportunityId={parsedOpportunityId} />
        </AppTabContent>
      </AppTab>
    </div>
  )
}
